import React, { useMemo } from 'react';
import { toast } from 'react-toastify';
import CustomModal from '../../../components/customModal/CustomModal';
import { Button, Form } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from "yup";
import './AddEditNdcWhiteListDialog.scss';
import useNdcWhitelistSetup from '../../../Hooks/useNdcWhitelistSetup';
import Loading from '../../../components/LoadingPage/Loading';
import { selectCurrentUserId } from '../../../redux/auth/authSlice';
import { useAppSelector } from '../../../redux/hooks';

interface AddEditNdcWhiteListDialogProps {
    isOpen: boolean;
    onClose: (isRefresh?: boolean) => void;
    selectedAthenaOrder: Record<string, any>;
    selectedNdcWhitelist?: Record<string, any> | null;
}

const AddEditNdcWhiteListDialog: React.FC<AddEditNdcWhiteListDialogProps> = ({
    isOpen,
    onClose,
    selectedAthenaOrder,
    selectedNdcWhitelist = null,
}) => {
    const isEditMode = selectedNdcWhitelist?.mode == 'edit';
    const isViewMode = selectedNdcWhitelist?.mode == 'view';
    const currentUserId = useAppSelector(selectCurrentUserId);
    const { upsertAthenaOrderNDCWhiteListAPI, upsertAthenaOrderNDCWhiteListLoading } = useNdcWhitelistSetup();

    const formik = useFormik({
        initialValues: {
            ndcCode: selectedNdcWhitelist?.ndcCode || '',
            isVerified: selectedNdcWhitelist?.isVerified || false,
        },
        enableReinitialize: true,
        validationSchema: Yup.object({
            ndcCode: Yup.string().trim().required('NDC Code is required'),
        }),
        onSubmit: async (values, { setSubmitting }) => {
            if (isViewMode) return;
            try {
                setSubmitting(true);
                const payload: Record<string, any> = {
                    athenaOrderMasterId: selectedAthenaOrder?.id,
                    ndcCode: values.ndcCode.trim(),
                    isVerified: values.isVerified,
                    isActive: true,
                };

                if (isEditMode && selectedNdcWhitelist) {
                    payload.id = selectedNdcWhitelist.id;
                    payload.updatedBy = currentUserId;
                } else {
                    payload.createdBy = currentUserId;
                }

                const res = await upsertAthenaOrderNDCWhiteListAPI(payload);
                if (res?.success) {
                    toast.success(`NDC whitelist record ${isEditMode ? 'updated' : 'added'} successfully.`);
                    handleClose(true);
                }
            } catch (error: any) {
                console.error(`Failed to ${isEditMode ? 'update' : 'add'} NDC whitelist record.`, error);
            } finally {
                setSubmitting(false);
            }
        },
    });

    const handleClose = (isRefresh = false) => {
        formik.resetForm();
        onClose(isRefresh);
    };

    const modalTitle = useMemo(() => (isViewMode ? 'View NDC Whitelist' : isEditMode ? 'Edit NDC Whitelist' : 'Add NDC Whitelist'), [isEditMode, isViewMode]);

    if (!isOpen) return null;

    return (
        <>
            {upsertAthenaOrderNDCWhiteListLoading && <Loading />}
            <CustomModal
                onClose={() => handleClose()}
                title={modalTitle}
                overlayClass='add-edit-ndc-whitelist-modal'
                content={
                    <Form onSubmit={formik.handleSubmit} id="add-edit-ndc-whitelist-form" className='add-edit-ndc-whitelist-modal__form'>
                        <Form.Group className='add-edit-ndc-whitelist-modal__form-group'>
                            <Form.Label>Order Type Id</Form.Label>
                            <Form.Control
                                type="text"
                                value={selectedAthenaOrder?.orderTypeId || ''}
                                disabled
                            />
                        </Form.Group>

                        <Form.Group className='add-edit-ndc-whitelist-modal__form-group'>
                            <Form.Label>Name</Form.Label>
                            <Form.Control
                                type="text"
                                value={selectedAthenaOrder?.name || ''}
                                disabled
                            />
                        </Form.Group>

                        <Form.Group className='add-edit-ndc-whitelist-modal__form-group'>
                            <Form.Label>NDC Code<span className='required'>*</span></Form.Label>
                            <Form.Control
                                type="text"
                                name="ndcCode"
                                placeholder="Enter NDC Code"
                                value={formik.values.ndcCode}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                disabled={isViewMode}
                                isInvalid={!!(formik.touched.ndcCode && formik.errors.ndcCode)}
                            />
                            <Form.Control.Feedback type="invalid">
                                {formik.errors.ndcCode as string}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className='add-edit-ndc-whitelist-modal__form-group add-edit-ndc-whitelist-modal__form-group--checkbox'>
                            <Form.Check
                                type="checkbox"
                                id="isVerified"
                                name="isVerified"
                                label="Verified"
                                checked={formik.values.isVerified}
                                onChange={formik.handleChange}
                                disabled={isViewMode}
                            />
                        </Form.Group>
                    </Form>
                }
                footer={
                    <div className="add-edit-ndc-whitelist-modal__footer">
                        <Button
                            variant="outline-primary"
                            onClick={() => handleClose()}
                        >
                            Cancel
                        </Button>
                        {!isViewMode && (
                            <Button
                                variant="primary"
                                type="submit"
                                form="add-edit-ndc-whitelist-form"
                            >
                                Save
                            </Button>
                        )}
                    </div>
                }
            />
        </>
    );
};

export default AddEditNdcWhiteListDialog;
